/**
 * @file 키비주얼 API
 * 2D 키비주얼 및 3D 행사장 콘텐츠(space)의 조회·업로드·수정·삭제·다운로드를 처리합니다.
 */

import { api } from "../../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const keyVisualApi = {
    // 2D 키비주얼
    /** 등록된 키비주얼 키워드 목록 조회 */
    getKeywords:      ()                    => api.get(`${API_BASE}/key-visual/2d/registered-keywords`),

    /** 전체 키비주얼 목록 조회 */
    getAll:           ()                    => api.get(`${API_BASE}/key-visual/2d/all`),

    /** 키비주얼 목록 페이지네이션 + 키워드/프로젝트명/이름 필터 (무한 스크롤 용)
     * @param {Object} params
     * @param {number} [params.page=0] - 0-based 페이지 번호
     * @param {number} [params.size=30] - 페이지 크기
     * @param {string[]} [params.keywords] - 키워드 (OR 매칭). 비우면 무시.
     * @param {string} [params.projectName] - 프로젝트명 부분 일치. 비우면 무시.
     * @param {string} [params.name] - 키비주얼명 부분 일치. 비우면 무시.
     */
    getAllPaged:      ({ page = 0, size = 30, keywords = [], projectName = "", name = "" } = {}) => {
        const params = { page, size };
        if (keywords && keywords.length > 0) params.keywords = keywords;
        if (projectName && projectName.trim()) params.projectName = projectName.trim();
        if (name && name.trim()) params.name = name.trim();
        return api.get(`${API_BASE}/key-visual/2d/list-paged`, params);
    },

    /** 키워드로 키비주얼 검색 (페이지네이션 없는 기존 엔드포인트)
     * @param {string[]} keywords - 검색할 키워드 배열
     */
    searchByKeywords: (keywords)            => api.get(`${API_BASE}/key-visual/2d/search`, { keywords }),

    /** 키비주얼 파일 업로드 (multipart/form-data)
     * @param {FormData} formData - 업로드할 파일이 담긴 FormData
     */
    upload:           (formData)            => api.upload(`${API_BASE}/key-visual/2d/upload`, formData),

    /** 키비주얼 확정 처리
     * @param {object} payload - 확정할 키비주얼 정보
     * @returns {Promise<number>} 생성된 키비주얼 id — 로고·레퍼런스 첨부에 필요하다.
     */
    confirm:          (payload)             => api.post(`${API_BASE}/key-visual/2d/confirm`, payload),

    /** 로고(BI·CI)·레퍼런스 이미지 첨부 — 여러 개를 한 번에.
     *
     * 키비주얼 '본체'(등록 시 1장)와 다른 파일이다. OCR 키워드 추출도 워터마크도 타지 않는다.
     *
     * @param {number} id - 키비주얼 ID (confirm 응답으로 받은 값)
     * @param {"LOGO"|"REFERENCE"} type - 첨부 종류
     * @param {File[]} files - 첨부할 파일들
     * @returns {Promise<string[]>} 추가 후 해당 종류의 전체 URL 목록
     */
    addAssets:        (id, type, files)     => {
        const fd = new FormData();
        (files || []).forEach((f, i) => {
            /*
             * File/Blob 이 아닌 값을 FormData 에 넣으면 브라우저가 String(f) 로 바꿔 버린다.
             * 객체면 그 결과가 "[object Object]" — 15바이트짜리 파일이 확장자·MIME 까지 멀쩡한 채로
             * S3 에 올라가고, 화면에는 깨진 이미지로만 보인다. 실제로 그렇게 올라간 적이 있어
             * (로고 2개가 15바이트로 저장됨) 여기서 막는다. 조용히 올리는 것보다 못 올리는 게 낫다.
             */
            if (!(f instanceof Blob)) {
                const kind = Object.prototype.toString.call(f);
                console.error("[keyVisualApi.addAssets] 파일이 아닌 값", i, kind, f);
                throw new Error(
                    `첨부 목록 ${i + 1}번째가 파일이 아닙니다(${kind}). 파일을 다시 선택해 주세요.`
                );
            }
            fd.append("files", f);
        });
        return api.upload(`${API_BASE}/key-visual/2d/${id}/assets?type=${type}`, fd);
    },

    /** 로고·레퍼런스 1개 제거
     * @param {number} id - 키비주얼 ID
     * @param {"LOGO"|"REFERENCE"} type - 첨부 종류
     * @param {string} path - 제거할 파일의 URL (목록에서 받은 값 그대로 넘기면 된다)
     * @returns {Promise<string[]>} 제거 후 남은 URL 목록
     */
    deleteAsset:      (id, type, path)      =>
        api.delete(`${API_BASE}/key-visual/2d/${id}/assets`, { params: { type, path } }),

    /** 키비주얼 수정 (프로젝트명·키워드)
     * @param {number} id - 키비주얼 ID
     * @param {object} payload - { projectName, keywords }
     */
    editKeyVisual:    (id, payload)         => api.patch(`${API_BASE}/key-visual/2d/edit/${id}`, payload),

    /** 2D/3D 통합 프로젝트명 자동완성 목록 */
    listProjectNames: ()                    => api.get(`${API_BASE}/key-visual/projects`),

    /** 키비주얼 삭제
     * @param {number} id - 키비주얼 ID
     */
    delete:           (id)                  => api.delete(`${API_BASE}/key-visual/2d/delete/${id}`),

    /** 키비주얼 파일 다운로드
     * @param {number} id - 키비주얼 ID
     */
    download:         (id)                  => api.download(`${API_BASE}/key-visual/2d/download/${id}`),

    // 3D 행사장 콘텐츠
    space: {
        /** 3D 콘텐츠 전체 목록 조회 */
        getList:      ()                         => api.get(`${API_BASE}/space-content/list`),

        /** 3D 콘텐츠 페이지네이션 + 5개 필터 (무한 스크롤 용)
         * @param {Object} params
         * @param {number} [params.page=0]
         * @param {number} [params.size=30]
         * @param {string} [params.locationType] - 실내/실외
         * @param {string[]} [params.themes] - 테마 (AND)
         * @param {string[]} [params.keywords] - 키워드 (AND)
         * @param {string} [params.eventName] - 행사명 부분 일치
         * @param {string} [params.location] - 위치 부분 일치
         */
        getListPaged: ({
            page = 0, size = 30,
            locationType, themes, keywords, eventName, location,
        } = {}) => {
            const p = { page, size };
            if (locationType) p.locationType = locationType;
            if (themes && themes.length > 0) p.themes = themes;
            if (keywords && keywords.length > 0) p.keywords = keywords;
            if (eventName) p.eventName = eventName;
            if (location) p.location = location;
            return api.get(`${API_BASE}/space-content/list-paged`, p);
        },

        /** 3D 콘텐츠 키워드 목록 조회 */
        getKeywords:  ()                         => api.get(`${API_BASE}/space-content/keywords`),

        /** 3D 콘텐츠 테마 목록 조회 */
        getThemes:    ()                         => api.get(`${API_BASE}/space-content/themes`),

        /** 행사명·장소로 3D 콘텐츠 파일 목록 조회
         * @param {string} eventName - 행사명
         * @param {string} location - 행사장 위치
         */
        getFiles:     (eventName, location)      =>
            api.get(`${API_BASE}/space-content/files`, { event: eventName, location }),

        /** 3D 콘텐츠 파일 업로드 (multipart/form-data)
         * @param {FormData} formData - 업로드할 파일이 담긴 FormData
         */
        upload:       (formData)                 => api.upload(`${API_BASE}/space-content/upload`, formData),

        /** 3D 콘텐츠 정보 수정
         * @param {number} id - 콘텐츠 ID
         * @param {object} payload - 수정할 데이터
         */
        edit:         (id, payload)              => api.patch(`${API_BASE}/space-content/edit/${id}`, payload),

        /** 3D 콘텐츠 삭제
         * @param {number} id - 콘텐츠 ID
         */
        delete:       (id)                       => api.delete(`${API_BASE}/space-content/${id}`),

        /** 3D 콘텐츠 ZIP 파일 일괄 다운로드
         * @param {object} params - 다운로드 조건 파라미터
         */
        downloadZip:  (params)                   => api.download(`${API_BASE}/space-content/download-zip`, params),

        /** 3D 콘텐츠 단일 파일 다운로드
         * @param {string} fileName - 다운로드할 파일명
         */
        downloadFile: (fileName)                 =>
            api.download(`${API_BASE}/space-content/download/${encodeURIComponent(fileName)}`),
    },
};
